import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import PublicationLinks from "../../../../components/Publications/components/PublicationLinks";
import SortableHeader from "../../../../components/atoms/SortableHeader";
import { getIconFromBool } from "./helpers";
import { sortStr, sortNum } from "../../../../common/helpers/helpers";
import LinkifyBase from "../../../../components/social/LinkifyBase";
import {
  pathogenicColor,
  likelyPathogenicColor,
  uncertainSignificanceColor,
  benignColor,
} from "../../../../common/helpers/colors";

const StyledTierCell = styled.span`
  color: ${(props) => props.color};
  font-weight: bold;
`;

const sortByTier = (a, b) => sortStr(a.amp_tier, b.amp_tier);
const sortByApprovalStatus = (a, b) =>
  sortStr(a.approval_status, b.approval_status);
const sortByEvidenceType = (a, b) => sortStr(a.evidence_type, b.evidence_type);
const sortByCapAscoEvidence = (a, b) =>
  sortStr(a.cap_asco_evidence_level, b.cap_asco_evidence_level);
const sortByIndication = (a, b) => sortStr(a.indication, b.indication);
const sortByResponseType = (a, b) => sortStr(a.response_type, b.response_type);
const sortByPubmedNumber = (a, b) =>
  sortNum(a.pub_med_references?.length, b.pub_med_references?.length);

const labels = {
  amp_tier: "Amp tier",
  approval_status: "Approval status",
  efficacy_evidence: "Efficacy Evidence",
  evidence_type: "Evidence Type",
  cap_asco_evidence_level: "Cap Asco Evidence Level",
  indication: "Indication",
  response_type: "Response Type",
  pub_med_references: "Pubmed References",
  therapy: "Therapy",
  polymorphism: "Polymorphism",
  transforming_activity: "Transforming Activity",
  associated_with_drug_resistance: "Drug Resistance",
};

const tierNames = {
  I: "Tier I",
  II: "Tier II",
  III: "Tier III",
  IV: "Tier IV",
  NA: "N/A",
};

const tierColors = {
  I: pathogenicColor,
  II: likelyPathogenicColor,
  III: uncertainSignificanceColor,
  IV: benignColor,
};

const getPubmed = (pubmedReferences) => {
  return <PublicationLinks ids={pubmedReferences} />;
};

const getTier = (tier) => {
  return (
    <StyledTierCell color={tierColors[tier]}>
      {tierNames[tier] ? tierNames[tier] : tier}
    </StyledTierCell>
  );
};

const JaxCkbTable = ({ items, sortedField, isAsc, toggleField }) => {
  if (!items || !items.length) return null;
  return (
    <table className="varsome-table varsome-table-sorted">
      <thead>
        <tr>
          <SortableHeader
            sort={(key) => toggleField(sortByTier, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="amp_tier"
            width="7%"
            align="left"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByApprovalStatus, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="approval_status"
            width="7%"
            align="left"
          />
          <SortableHeader
            sort={() => {}}
            labels={labels}
            sortedField={sortedField}
            isSortable={false}
            showSortLabel={false}
            isAsc={isAsc}
            field="efficacy_evidence"
            width="21%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByEvidenceType, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="evidence_type"
            width="6%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByCapAscoEvidence, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="cap_asco_evidence_level"
            width="7%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByIndication, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="indication"
            width="10%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByResponseType, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="response_type"
            width="7%"
            align="center"
          />
          <SortableHeader
            sort={(key) => toggleField(sortByPubmedNumber, key)}
            labels={labels}
            sortedField={sortedField}
            isAsc={isAsc}
            field="pub_med_references"
            width="7%"
            align="center"
          />
          <SortableHeader
            sort={() => {}}
            labels={labels}
            isSortable={false}
            showSortLabel={false}
            sortedField={sortedField}
            isAsc={isAsc}
            field="therapy"
            width="7%"
            align="center"
          />
          <SortableHeader
            sort={() => {}}
            labels={labels}
            isSortable={false}
            showSortLabel={false}
            sortedField={sortedField}
            isAsc={isAsc}
            field="polymorphism"
            width="7%"
            align="center"
          />
          <SortableHeader
            sort={() => {}}
            labels={labels}
            isSortable={false}
            showSortLabel={false}
            sortedField={sortedField}
            isAsc={isAsc}
            field="transforming_activity"
            width="7%"
            align="center"
          />
          <SortableHeader
            sort={() => {}}
            labels={labels}
            isSortable={false}
            showSortLabel={false}
            sortedField={sortedField}
            isAsc={isAsc}
            field="associated_with_drug_resistance"
            width="7%"
            align="center"
          />
        </tr>
      </thead>
      <tbody>
        {items.map((item, i) => (
          <tr
            key={`${item?.amp_tier}-${item?.approval_status}-${item?.therapy_id}-${item?.therapy}-${i}`}
          >
            <td className="varsome-text-center">
              {item.amp_tier ? getTier(item.amp_tier) : ""}
            </td>
            <td className="varsome-text-center">
              {item.approval_status ? (
                <LinkifyBase txt={item.approval_status} />
              ) : (
                "-"
              )}
            </td>
            <td className="">
              {item.efficacy_evidence ? (
                <LinkifyBase txt={item.efficacy_evidence} />
              ) : (
                "-"
              )}
            </td>
            <td className="varsome-text-center">
              {item.evidence_type ? (
                <LinkifyBase txt={item.evidence_type} />
              ) : (
                "-"
              )}
            </td>
            <td className="varsome-text-center">
              {item.cap_asco_evidence_level ? (
                <LinkifyBase txt={item.cap_asco_evidence_level} />
              ) : (
                "-"
              )}
            </td>
            <td className="varsome-text-center">
              {item.indication ? <LinkifyBase txt={item.indication} /> : "-"}
            </td>
            <td className="varsome-text-center">
              {item.response_type ? (
                <LinkifyBase txt={item.response_type} />
              ) : (
                "-"
              )}
            </td>
            <td className="varsome-text-center">
              {item.pub_med_references?.length
                ? getPubmed(item.pub_med_references)
                : "-"}
            </td>
            <td className="varsome-text-center">
              {item.therapy?.length ? <LinkifyBase txt={item.therapy} /> : "-"}
            </td>

            <td className="varsome-text-center">
              {getIconFromBool(item?.polymorphism)}
            </td>
            <td className="varsome-text-center">
              {getIconFromBool(item?.transforming_activity)}
            </td>
            <td className="varsome-text-center">
              {getIconFromBool(item?.associated_with_drug_resistance)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

JaxCkbTable.propTypes = {
  labels: PropTypes.shape({
    pubMedReferences: PropTypes.string,
    date: PropTypes.string,
    pValue: PropTypes.string,
    diseaseOrTrait: PropTypes.string,
    strongestSnpRiskAllele: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      amp_tier: PropTypes.string,
      approval_status: PropTypes.string,
      cap_asco_evidence_level: PropTypes.string,
      efficacy_evidence: PropTypes.string,
      evidence_type: PropTypes.string,
      indication: PropTypes.string,
      normalized_cancer: PropTypes.string,
      normalized_drug: PropTypes.string,
      pub_med_references: PropTypes.arrayOf(PropTypes.number),
      response_type: PropTypes.string,
      therapy: PropTypes.string,
      therapy_id: PropTypes.number,
    })
  ),
  sortedField: PropTypes.string,
  isAsc: PropTypes.bool,
  toggleField: PropTypes.func.isRequired,
};

JaxCkbTable.defaultProps = {
  labels: {},
  items: [],
  sortedField: null,
  isAsc: true,
};

export default JaxCkbTable;
